import React from 'react';
import { UI } from '@gik/ui';
import ModalButtonFooter from '@gik/ui/Modal/ModalButtonFooter';
import { ICalendarEventFormValues } from '../components/EventForm/CalendarEventForm';
import { ICalendarAnnouncement, ICalendarEntry, ICalendarEvent } from '../models/Calendar';
import CalendarEditEventForm from '../components/EventForm/CalendarEditEventForm';
import { CalendarEventCreateFlow } from '../components/EventCreateForm/CalendarEventCreateFlow';
import { calendarStoreApi } from '../store/CalendarStore';
import {
  markClaimedEventIdBeforeLoggedIn,
  markClaimedEventStartDateBeforeLoggedIn,
  markClaimedEventMonthBeforeLoggedIn,
  getClaimedEventIdBeforeLoggedIn,
  getClaimedEventStartDateBeforeLoggedIn,
} from '@gik/core/utils/Tracking';
import { userStoreApi } from '@gik/core/store/UserStore';
import { Moment } from 'moment';
import { auth } from '@gik/auth';
// import { ICalendarEventsResponse } from '../components/Calendar/CalendarDaysList';
import { CalendarEditAnnouncementForm } from '../components/Announcements/CalendarEditAnnouncementForm';
import { getAnnouncementUIContextFromId } from '../components/Announcements';
import { ICalendarEntriesResponse } from '@gik/api/calendar/calendar';
import CalendarClaimView from '../components/ClaimEvent/CalendarClaimView';
import { CalendarClaimEvent } from '../components/ClaimEvent/CalendarClaimEvent';
import { CalendarAnnouncementViewWrapper } from '../components/Announcements/CalendarAnnouncementViewWrapper';
import moment from 'moment';

export function add(date?: Moment) {
  const footerId = 'CalendarEventAddFormFooter';

  // reset whether the user has changed gift cards
  calendarStoreApi.getState().setHasChangedGiftCards(false);

  return UI.dialog<ICalendarEventFormValues>(
    ({ close }) => {
      return (
        <CalendarEventCreateFlow
          date={date}
          buttonsPortal={() => document.getElementById(footerId)}
          onSuccess={async (newEventId: string, addMore: boolean) => {
            close(true);
            await calendarStoreApi.getState().reloadFunction();
            calendarStoreApi.getState().setFormSaving(false);

            setTimeout(() => {
              calendarStoreApi.getState().scrollToEntryById(newEventId);
            }, 300);

            if (addMore) {
              add(date);
            }
          }}
        />
      );
    },
    {
      title: 'Add Request',
      closable: true,
      autowidth: false,
      modalProps: {
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
      },
      className: 'gik-calendar-modal gik-calendar-add-event',
      footer: <ModalButtonFooter centeredButtons={false} id={footerId} />,
    }
  );
}

export async function edit(event: ICalendarEvent, entry: ICalendarEntry) {
  const footerId = 'CalendarEventEditFormFooter';

  // const typesResponse = await getCalendarEventTypes();
  // const calendarEventTypes = await typesResponse.json();

  // const calendarEventType = calendarEventTypes.find(v => v.id === event.typeId);
  // if (!calendarEventType) {
  //   throw new Error('No Wordpress Calendar event type found matching ID: ' + event.typeId);
  // }

  // TODO: use footerRef
  const response: ICalendarEventFormValues = await UI.dialog<ICalendarEventFormValues>(
    ({ close }) => {
      return (
        <CalendarEditEventForm
          event={event}
          entry={entry}
          // TODO: pass footerRef instead
          buttonsPortal={() => document.getElementById(footerId)}
          onSuccess={() => {
            close(true);
            calendarStoreApi.getState().reloadFunction();
          }}
          onDelete={() => {
            close(false);
            calendarStoreApi.getState().reloadFunction();
          }}
        />
      );
    },
    {
      title: 'Edit Request',
      closable: true,
      autowidth: false,
      modalProps: {
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
      },
      className: 'gik-calendar-modal gik-calendar-edit-event',
      // TODO: we should not need to pass footer here because we should use ModalButtonFooter as default in Dialogs
      footer: <ModalButtonFooter id={footerId} />,
    }
  );

  return response;
}

export async function editAnnouncement(event: ICalendarEvent, entry: ICalendarAnnouncement) {
  const footerId = 'CalendarEventEditFormFooter';

  const response = await UI.dialog(
    ({ close }) => {
      return (
        <CalendarEditAnnouncementForm
          entry={entry}
          event={event}
          buttonsPortal={() => document.getElementById(footerId)}
          onSuccess={() => {
            close(true);
            calendarStoreApi.getState().reloadFunction();
          }}
          onDelete={() => {
            close(false);
            calendarStoreApi.getState().reloadFunction();
          }}
        />
      );
    },
    {
      title: 'Edit Announcement',
      closable: true,
      autowidth: false,
      modalProps: {
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
      },
      className: 'gik-calendar-modal gik-calendar-edit-announcement',
      footer: <ModalButtonFooter id={footerId} />,
    }
  );

  return response;
}

export async function viewAnnouncement(event: ICalendarEvent, entry: ICalendarAnnouncement) {
  const footerId = 'CalendarEventEditFormFooter';

  const typeContext = getAnnouncementUIContextFromId(entry.announcementTypeId);

  const response = await UI.dialog(
    ({ close }) => {
      return (
        <CalendarAnnouncementViewWrapper
          startsAt={event.startsAt}
          endsAt={event.endsAt}
          entryId={event.entryId}
          allDay={entry.allDay}
          typeId={entry.announcementTypeId}
          description={entry.description}
          title={entry.title}
          typeContext={typeContext}
          buttonsPortal={() => document.getElementById(footerId)}
          onDone={close}
          onEdit={() => {
            close();
            setTimeout(() => {
              editAnnouncement(event, entry);
            }, 300);
          }}
          onDeleted={() => {
            close();
            calendarStoreApi.getState().reloadFunction();
          }}
        />
      );
    },
    {
      title: 'Announcement',
      closable: true,
      autowidth: false,
      className: 'gik-calendar-modal gik-calendar-view-announcement',
      footer: () => <ModalButtonFooter id={footerId} />,
    }
  );

  return response;
}

export const calendarEventClaimFormContentId = 'CalendarEventClaimFormContent';

export async function claim(event: ICalendarEvent, entry: ICalendarEntry) {
  const calendarStore = calendarStoreApi.getState();
  // if the user is not logged in yet store this entry id and start date so we can
  // show the login dialog and return the user to this claim after signup
  if (!userStoreApi.getState().id) {
    markClaimedEventIdBeforeLoggedIn(entry.id);
    // child instances share the same parent id and can only be identified by date
    markClaimedEventStartDateBeforeLoggedIn(event.startsAt);
    markClaimedEventMonthBeforeLoggedIn(calendarStore.selectedMonth);
    auth.login();

    return false;
  }

  const headerId = 'CalendarEventClaimFormHeader';
  const footerId = 'CalendarEventClaimFormFooter';

  // get selected eventType
  // const eventType = calendarStoreApi
  //   .getState()
  //   .calendarEventTypes.find(eventType => eventType.id === event.typeId);

  function reloadMonth() {
    calendarStoreApi.getState().reloadFunction(calendarStoreApi.getState().selectedMonth);
  }

  const response: ICalendarEventFormValues = await UI.dialog<ICalendarEventFormValues>(
    ({ close, setTitle, setOnBack }) => {
      const [selectedEvent, setSelectedEvent] = React.useState(event);
      const [selectedEntry, setSelectedEntry] = React.useState(entry);

      return (
        <CalendarClaimEvent
          modalContentId={calendarEventClaimFormContentId}
          event={selectedEvent}
          entry={selectedEntry}
          setSelectedEvent={setSelectedEvent}
          setSelectedEntry={setSelectedEntry}
          stepsNavPortal={() => document.getElementById(headerId)}
          buttonsPortal={() => document.getElementById(footerId)}
          updateModalTitle={setTitle}
          setOnDialogBack={setOnBack}
          onSuccess={() => {
            reloadMonth();
          }}
          onFailed={() => {
            reloadMonth();
          }}
          onConflict={() => {
            reloadMonth();
          }}
          onEditSuccess={() => {
            close(true);
            reloadMonth();
          }}
          onUnclaim={() => {
            close(true);
            reloadMonth();
          }}
          onClose={() => {
            close(true);
          }}
        />
      );
    },
    {
      title: '', // set in CalendarClaimEvent
      closable: true,
      modalProps: {
        shouldCloseOnEsc: false,
        shouldCloseOnOverlayClick: false,
      },
      autowidth: false,
      padding: true,
      className: 'gik-claim-modal gik-calendar-claim-event-modal',
      headerId: headerId,
      contentId: calendarEventClaimFormContentId,
      footer: <ModalButtonFooter id={footerId} centeredButtons={true} />,
      onClose: () => {
        calendarStoreApi.getState().setClaimFailErrorCode(null);
        calendarStoreApi.getState().setPaymentConfirmationValues(null);
        return true;
      },
    }
  );

  // reset calendar store values to reset the modal
  calendarStoreApi.getState().resetStore();

  return response;
}

export function viewClaim(entry: ICalendarEntry, event: ICalendarEvent) {
  const footerId = 'CalendarEventClaimViewFooter';

  UI.dialog(
    ({ close }) => (
      <CalendarClaimView
        buttonsPortal={() => document.getElementById(footerId)}
        entry={entry}
        event={event}
        onClose={() => {
          close(true);
        }}
      />
    ),
    {
      // TODO: translation do not seem to work here
      title: 'Request Details',
      closable: true,
      className: 'gik-claim-modal gik-calendar-view-claim-event-modal',
      autowidth: false,
      padding: true,
      footer: <ModalButtonFooter id={footerId} centeredButtons={true} />,
    }
  );
}

// eslint-disable-next-line
export function continueClaimAfterLogin(
  response: ICalendarEntriesResponse,
  userCreatedDate: Moment,
  inkindPageTitle: string
) {
  if (!response) return;
  const claimedEventId = getClaimedEventIdBeforeLoggedIn();
  const claimedEventStartDate = getClaimedEventStartDateBeforeLoggedIn();
  const userId = userStoreApi.getState().id;

  if (!claimedEventId || !userId) return;

  const event = response.events.find((e: ICalendarEvent) => e.startsAt === claimedEventStartDate);
  const entry = response.entries.find((e: ICalendarEntry) => e.id === claimedEventId);

  if (!event || !entry) return;

  markClaimedEventIdBeforeLoggedIn(null);
  markClaimedEventStartDateBeforeLoggedIn(null);

  if (entry.claim) {
    UI.notifyError('This event has already been claimed.');
    return;
  }

  const isNewAccount = moment.utc().diff(userCreatedDate, 'minutes') < 5;
  const message = isNewAccount
    ? 'Thank you for creating a Give InKind account. You are now following the page ' +
      inkindPageTitle +
      ' and will receive updates submitted by the organizer. Please continue claiming your item...'
    : 'Thank you for logging in. Please continue claiming your item...';

  UI.notifySuccess(message, { autoCloseTime: isNewAccount ? 10000 : 5000 });

  claim(event, entry);

  document.getElementsByClassName('gik-inkind-calendar')[0].scrollIntoView();
}
